import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Popconfirm,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  createMenuReward,
  getMenuItems,
  getMenuRewards,
  updateMenuReward,
  deleteMenuReward,
} from "../actions/merchant_actions";
import { useTranslation } from "react-i18next";

const MenuRewards = () => {
  const { t } = useTranslation();
  const accUrn = useSelector((state) => state.account.urn);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [skuOptions, setSkuOptions] = useState([]);
  const [form] = Form.useForm();

  const {
    refetch: refetchRewards,
    isLoading: merchantLoading,
    data,
    isFetching,
  } = useQuery({
    queryKey: ["merchant", accUrn],
    queryFn: () => getMenuRewards({ account_urn: accUrn }),
    enabled: !!accUrn,
  });

  const { isLoading, data: itemsData } = useQuery({
    queryKey: ["menuItems", accUrn],
    queryFn: () => getMenuItems({ account_urn: accUrn }),
    enabled: !!accUrn,
  });

  useEffect(() => {
    if (itemsData) {
      const options = itemsData.map((item) => ({
        label: `${item.data.title} (${item.data.sku})`,
        value: item.data.sku,
      }));
      setSkuOptions(options);
    }
  }, [itemsData]);

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      skus: record.skus,
      number_of_stamps: parseInt(record.number_of_stamps, 10),
    });
    setIsModalOpen(true);
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const payload = {
        skus: values.skus.join(","),
        number_of_stamps: values.number_of_stamps,
      };

      const action = editingRecord
        ? updateMenuReward({
            account_urn: accUrn,
            urn: editingRecord.urn,
            data: payload,
          })
        : createMenuReward({
            account_urn: accUrn,
            data: [payload],
          });

      action
        .then(() => {
          message.success(
            editingRecord ? t("reward_updated") : t("reward_created")
          );
          refetchRewards();
          setIsModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        })
        .catch(() => {
          message.error(
            editingRecord
              ? t("failed_update_reward")
              : t("failed_create_reward")
          );
        });
    });
  };

  const handleDelete = (record) => {
    deleteMenuReward({
      account_urn: accUrn,
      urn: record.urn,
    })
      .then(() => {
        message.success(t("reward_deleted"));
        refetchRewards();
      })
      .catch(() => {
        message.error(t("failed_delete_reward"));
      });
  };

  const columns = [
    {
      title: t("mode"),
      dataIndex: "mode",
      key: "mode",
      render: (mode) => <Tag color="blue">{mode}</Tag>,
    },
    {
      title: t("skus"),
      dataIndex: "skus",
      key: "skus",
      render: (skus) =>
        skus.map((sku) => (
          <Tag key={sku} color="purple">
            {sku}
          </Tag>
        )),
    },
    {
      title: t("type"),
      dataIndex: "type",
      key: "type",
      render: (type) => <Tag color="green">{type}</Tag>,
    },
    {
      title: t("stamps"),
      dataIndex: "number_of_stamps",
      key: "number_of_stamps",
    },
    {
      title: t("discount_percent"),
      dataIndex: "allowed_discount_percent",
      key: "allowed_discount_percent",
    },
    {
      title: t("voucher_scheme"),
      dataIndex: "voucher_loyalty_scheme_slug",
      key: "voucher_loyalty_scheme_slug",
      render: (slug, record) => (
        <Tooltip title={`${t("urn")}: ${record.voucher_loyalty_scheme_urn}`}>
          {slug}
        </Tooltip>
      ),
    },
    {
      title: t("stamp_card_scheme"),
      dataIndex: "stamp_card_loyalty_scheme_slug",
      key: "stamp_card_loyalty_scheme_slug",
      render: (slug, record) => (
        <Tooltip title={`${t("urn")}: ${record.stamp_card_loyalty_scheme_urn}`}>
          {slug}
        </Tooltip>
      ),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEditModal(record)}>
            {t("update")}
          </Button>
          <Popconfirm
            title={t("confirm_delete_reward")}
            onConfirm={() => handleDelete(record)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="link" danger>
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "1rem" }}>
      <Space
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          {t("create_reward")}
        </Button>
      </Space>

      <Table
        rowKey={(record) => record.urn}
        columns={columns}
        dataSource={data?.data?.body}
        pagination={false}
        bordered
        scroll={{ x: "max-content" }}
        size="middle"
      />

      <Modal
        title={editingRecord ? t("update_reward") : t("create_reward")}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        okText={editingRecord ? t("update") : t("create")}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t("sku")}
            name="skus"
            rules={[{ required: true, message: t("select_at_least_one_sku") }]}
          >
            <Select
              mode="multiple"
              placeholder={t("select_products_by_sku")}
              options={skuOptions}
              loading={isLoading || merchantLoading || isFetching}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label={t("number_of_stamps")}
            name="number_of_stamps"
            rules={[{ required: true, message: t("enter_number_of_stamps") }]}
          >
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              placeholder={t("enter_stamp_count")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuRewards;
